import axios, { AxiosError } from 'axios';
import type {
    Order,
    Product,
    CreateProductDto,
    UpdateProductDto,
    SiteSettings,
    PaginatedResponse,
    StrikeDto,
    CategoryDto,
    SpecificationDto,
    UpdateUserProfileDto,
    UserProfileDto,
    AccountStatus,
    ChildDto,
    CreateChildDto,
    UpdateChildDto,
    ChecklistPhase,
    GrowthInsightDto,
    ReportDto,
    SellerRatingDto,
    SellerSummaryDto,
    SellerReviewDto,
    SellerRatingBreakdownDto,
    PendingRatingDto,
    OrderRatingStatusDto,
    PublicUserProfileDto,
    CreateTicketDto,
    CheckoutResponseDto,
    CreateCheckoutDto,
    ListingAssistResultDto,
    NotificationDto,
    LanguageDto,
    SidebarFilterDto,
    CreatePartnerInquiryDto,
    ShopMarketplaceLocale,
} from '../types';

// Runtime guard removed (separation now enforced via explicit imports in shop vs admin code)

export const shopAxios = axios.create({
    baseURL: '/api',
    withCredentials: true
});

const CREDENTIAL_AUTH_PATHS = [
    '/auth/user-login',
    '/auth/user-register',
    '/auth/google',
    '/auth/google-config',
    '/auth/forgot-password',
    '/auth/confirm-email',
    '/auth/resend-confirmation',
    '/auth/reset-password',
];

function isCredentialAuthRequest(url?: string): boolean {
    if (!url) return false;
    return CREDENTIAL_AUTH_PATHS.some((path) => url.includes(path));
}

// NEW: Start of silent refresh interceptor (handles 401 by refreshing cookies and retrying)
let isRefreshing = false;
let failedQueue: { resolve: (value?: unknown) => void; reject: (reason?: unknown) => void }[] = [];

const processQueue = (error: unknown = null) => {
    failedQueue.forEach(prom => error ? prom.reject(error) : prom.resolve());
    failedQueue = [];
};

shopAxios.interceptors.response.use(
    response => response,
    async (error: AxiosError) => {
        const originalRequest = error.config; // Type assertion for _retry flag

        // === CRITICAL FIXES ===
        if (!originalRequest) {
            return Promise.reject(error);
        }

        // 1. Never try to refresh the refresh endpoint itself (prevents infinite loop)
        if (originalRequest.url?.includes('/auth/refresh')) {
            return Promise.reject(error);
        }

        // Login/register/forgot 401s are credentials errors, not an expired session.
        // Refreshing here delayed "Logging in…" and hid invalid-password (L-01).
        if (isCredentialAuthRequest(originalRequest.url)) {
            return Promise.reject(error);
        }

        // 2. Only handle 401s
        if (error.response?.status !== 401) {
            return Promise.reject(error);
        }

        // 3. If we already retried this exact request, fail immediately
        if (originalRequest._retry) {
            return Promise.reject(error);
        }

        if (isRefreshing) {
            // Queue the request until refresh finishes
            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            })
                .then(() => shopAxios(originalRequest))
                .catch(err => Promise.reject(err));
        }

        // Start refresh
        originalRequest._retry = true;
        isRefreshing = true;

        try {
            await shopAxios.post('/auth/refresh'); // Cookies are sent automatically
            processQueue();                        // Resolve all queued requests
            return shopAxios(originalRequest);     // Retry original request (now with new token)
        } catch (refreshError) {
            processQueue(refreshError);
            console.error('Refresh failed (auth token invalid/expired):', refreshError);
            // The ShopAuthContext 'user' query will hit 401 on next poll/usage and return null.
            // The user ID change effect + user-keyed queryKeys will then clear all private caches
            // (myProducts, favourites, notifications etc). This prevents any cross-account bleed.
            // We attempt server-side logout cleanup.
            try { await userLogout(); } catch {}
            return Promise.reject(refreshError);
        } finally {
            isRefreshing = false;
        }
    }
);

// NEW: End of silent refresh interceptor

// Shared: Logout functions
export const userLogout = async (): Promise<void> => {
    await shopAxios.post('/auth/logout');
};

// User Profile (user-facing)
export const getUserProfile = () => shopAxios.get<UserProfileDto>('/user/profile').then(res => res.data);
export const updateUserProfile = (data: UpdateUserProfileDto) => shopAxios.put('/user/profile', data);
export const checkUsernameAvailable = (username: string) =>
    shopAxios
        .get<{ available: boolean; reason?: string }>(
            `/auth/check-username?username=${encodeURIComponent(username)}`
        )
        .then((res) => res.data);

export type UsernameSuggestions = {
    wordPairs: string[];
    firstNameHandle: string | null;
    chips: string[];
};

export const getUsernameSuggestions = (opts?: {
    firstName?: string;
    language?: string;
    exclude?: string[];
}) => {
    const params = new URLSearchParams();
    if (opts?.firstName?.trim()) params.set('firstName', opts.firstName.trim());
    if (opts?.language?.trim()) params.set('language', opts.language.trim());
    if (opts?.exclude?.length) params.set('exclude', opts.exclude.join(','));
    const q = params.toString();
    return shopAxios
        .get<UsernameSuggestions>(`/auth/username-suggestions${q ? `?${q}` : ''}`)
        .then((res) => res.data);
};
/** Idempotent: mark one product tour complete (home | mypetito). */
export const completeProductTour = (tourId: 'home' | 'mypetito') =>
    shopAxios.post('/user/product-tour/complete', { tourId });
export const recordChildDataConsent = () => shopAxios.post('/user/child-data-consent');
export const snoozeMyPetitoNudge = () => shopAxios.post('/user/mypetito-nudge/snooze');
export const disableMyPetitoNudges = () => shopAxios.post('/user/mypetito-nudge/disable');

export interface ShopLoginPromptQueue {
    gapMinutes: number;
    prompts: { id: string; sortOrder: number; enabled: boolean }[];
}

/** Public order of on-login prompts. Admin writes a separate endpoint. */
export const getLoginPromptQueue = () =>
    shopAxios.get<ShopLoginPromptQueue>('/login-prompts/queue').then((res) => res.data);

/** Starts the gap. Does not open the next prompt. */
export const markLoginPromptClosed = () =>
    shopAxios
        .post<{ lastLoginPromptClosedAt: string }>('/user/login-prompt-closed')
        .then((res) => res.data);
/** Account status + marketplace access (ban wind-down). */
export type UserStatusResponse = {
    status: AccountStatus;
    accessLevel: 'Full' | 'LimitedWindDown' | 'LockedOut' | 'Suspended' | string;
};

export const getUserStatus = () =>
    shopAxios.get<UserStatusResponse | AccountStatus>('/user/status').then(res => {
        const data = res.data;
        // Backward compatible: older API returned bare status enum/string
        if (data && typeof data === 'object' && 'status' in data) {
            return data as UserStatusResponse;
        }
        return { status: data as AccountStatus, accessLevel: 'Full' } satisfies UserStatusResponse;
    });
export const getUserStrikes = () => shopAxios.get<StrikeDto[]>('/user/strikes').then(res => res.data);
//reports
export const getSellerSummary = (sellerId: string) =>
    shopAxios.get<SellerSummaryDto>(`/ratings/seller/${sellerId}`).then(res => res.data);

export const getSellerReviews = (sellerId: string, page = 1, pageSize = 20) =>
    shopAxios
        .get<SellerReviewDto[]>(`/ratings/seller/${sellerId}/reviews`, {
            params: { page, pageSize },
        })
        .then(res => res.data);

export const getSellerBreakdown = (sellerId: string) =>
    shopAxios
        .get<SellerRatingBreakdownDto>(`/ratings/seller/${sellerId}/breakdown`)
        .then(res => res.data);

export const getPendingRatings = () =>
    shopAxios.get<PendingRatingDto[]>(`/ratings/pending`).then(res => res.data);

export const getOrderRatingStatus = (orderId: number) =>
    shopAxios
        .get<OrderRatingStatusDto>(`/ratings/order/${orderId}/status`)
        .then(res => res.data);

export const ensureRateReminder = (orderId: number) =>
    shopAxios.post(`/ratings/order/${orderId}/remind`);

export const submitSellerRating = (model: SellerRatingDto) =>
    shopAxios.post(`/ratings`, model);

export const submitReport = (model: ReportDto) =>
    shopAxios.post(`/reports`, model);

export const submitTicket = (model: CreateTicketDto) =>
    shopAxios.post<{ id: number; message: string }>(`/tickets`, model).then(res => res.data);

export const getPublicUserProfile = (userId: string) =>
    shopAxios
        .get<PublicUserProfileDto>(`/user/${userId}/public-profile`)
        .then(res => res.data);

export const getPublicUserListings = (userId: string) =>
    shopAxios.get<Product[]>(`/user/${userId}/listings`).then(res => res.data);
// Products
export type GetProductsParams = {
    page?: number;
    limit?: number;
    search?: string;
    filter?: string;
    top?: number;
    category?: string;
    minPrice?: number;
    maxPrice?: number;
    condition?: string;
    conditions?: string[];
    specFilters?: string[];
    /** @deprecated use growthPhaseIds */
    growthPhaseId?: number;
    growthPhaseIds?: number[];
    /** Free dual-handle age window (months); ignored by server if growth phases are set. */
    ageMinMonths?: number;
    ageMaxMonths?: number;
    /** When set, server excludes this seller's listings (own products). */
    excludeSellerId?: string;
};

export const getProducts = (params: GetProductsParams = { page: 1, limit: 20 }) => {
    const { specFilters, conditions, growthPhaseIds, growthPhaseId, ageMinMonths, ageMaxMonths, ...rest } = params;
    const searchParams = new URLSearchParams();
    Object.entries(rest).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            searchParams.append(key, String(value));
        }
    });
    if (conditions?.length) {
        conditions.forEach((c) => searchParams.append('conditions', c));
    }
    if (specFilters?.length) {
        specFilters.forEach((filter) => searchParams.append('specFilters', filter));
    }
    if (growthPhaseIds?.length) {
        growthPhaseIds.forEach((id) => searchParams.append('growthPhaseIds', String(id)));
    } else if (growthPhaseId != null) {
        searchParams.append('growthPhaseId', String(growthPhaseId));
    } else if (ageMinMonths != null && ageMaxMonths != null) {
        searchParams.append('ageMinMonths', String(ageMinMonths));
        searchParams.append('ageMaxMonths', String(ageMaxMonths));
    }
    const query = searchParams.toString();
    const url = query ? `/products?${query}` : '/products';
    return shopAxios.get<PaginatedResponse<Product>>(url).then(res => res.data);
};

export const getProduct = (id: number) => shopAxios.get<Product>(`/products/${id}`).then(res => res.data); // For ProductDetail.tsx

export const createProduct = (data: CreateProductDto) => shopAxios.post<Product>('/products', data).then(res => res.data);

export const updateProduct = (id: number, data: UpdateProductDto) => shopAxios.put<Product>(`/products/${id}`, data).then(res => res.data);

//images
// NEW: Upload images separately - returns real S3 URLs
export const uploadProductImages = async (files: File[]): Promise<string[]> => {
    if (!files.length) return [];
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    const response = await shopAxios.post('/images/upload', formData);
    return response.data;
};

/** Label OCR prefill — does not store images on S3. */
export const analyzeListingTagImages = async (files: File[]): Promise<ListingAssistResultDto> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    const response = await shopAxios.post<ListingAssistResultDto>('/listing-assist/analyze', formData);
    return response.data;
};

export const deleteMyProduct = (id: number) => shopAxios.delete(`/products/${id}`);

export const getMyProducts = () => shopAxios.get<Product[]>('/products/my-products').then(res => res.data);

// Orders
export const getMyOrders = () => shopAxios.get<Order[]>('/orders/my-orders').then(res => res.data);
export const getMySales = () => shopAxios.get<Order[]>('/orders/my-sales').then(res => res.data);
// Orders - shop side (add this)
export const requestOrderRefund = (orderId: number, reason: string) =>
    shopAxios
        .post<Order>(`/orders/${orderId}/request-refund`, { reason })
        .then((res) => res.data);

export const getOrderById = (id: number) =>
    shopAxios.get<Order>(`/orders/${id}`).then(res => res.data);

export const shipOrder = (
    orderId: number,
    data: { carrier: string; trackingNumber: string; trackingUrl?: string }
) => shopAxios.post<Order>(`/orders/${orderId}/ship`, data).then((res) => res.data);

export const createOrderShippingLabel = (orderId: number) =>
    shopAxios.post<Order>(`/orders/${orderId}/shipping-label`).then((res) => res.data);

export const createOrder = (data: { productId: number }) => shopAxios.post<Order>('/orders', data).then(res => res.data); // Simple, expand as needed

//shopify
// NEW checkout call
export const createCheckout = (data: CreateCheckoutDto) =>
    shopAxios.post<CheckoutResponseDto>('/orders/checkout', data).then(res => res.data);

// Categories (Public for product creation)
export const getAllCategories = () =>
    shopAxios.get<{ data: CategoryDto[]; dataVersion: number }>('/categories').then(res => res.data.data); // Assume public endpoint; if not, move to admin and protect

/** Active package size bands for listing form (no Sendcloud method ids). */
export const getShippingSizeBands = () =>
    shopAxios.get<import('../types').ShippingSizeBandPublicDto[]>('/shipping-size-bands').then((res) => res.data);

export type PickupLocationPublicDto = { id: number; city: string; region?: string | null };

export const getPickupLocations = () =>
    shopAxios.get<PickupLocationPublicDto[]>('/pickup-locations').then((res) => res.data);

// Specifications (Public helper for product form)
export const getSpecificationsForCategories = (categoryNames: string[]) =>
    shopAxios.post<SpecificationDto[]>('/specifications/for-categories', { categoryNames }).then(res => res.data); // Assume public; secure if sensitive

// Children
export const getMyChildren = () => shopAxios.get<ChildDto[]>('/children');
export const createChild = (dto: CreateChildDto) => shopAxios.post<ChildDto>('/children', dto);
export const updateChild = (id: number, dto: UpdateChildDto) => shopAxios.put<ChildDto>(`/children/${id}`, dto);
export const deleteChild = (id: number) => shopAxios.delete(`/children/${id}`);
export const getChecklistPhases = () =>
    shopAxios.get<ChecklistPhase[]>('/children/phases').then(res => res.data);
// Updated recommendation call \u2014 now accepts horizonMonths
//export const getRecommendationsForChild = (childId: number, horizonMonths: number = 0) =>
//    shopAxios.get<Product[]>(`/children/${childId}/recommendations?horizonMonths=${horizonMonths}`);
// api.ts
export const getRecommendationsForChild = (childId: number, phaseId: number) =>
    shopAxios.get<Product[]>(`/children/${childId}/recommendations?phaseId=${phaseId}`)
        .then(res => res.data);

export const getGrowthInsight = (childId: number, phaseId: number) =>
    shopAxios.get<GrowthInsightDto>(`/children/${childId}/growth-insight?phaseId=${phaseId}`)
        .then(res => res.data);

export type CurrentMeasurementSuggestionDto = {
    childId: number;
    childName: string;
    hasStoredMeasurements: boolean;
    isStale: boolean;
    calendarAgeMonths: number;
    fitAgeMonths?: number | null;
    suggestedHeightCm?: number | null;
    suggestedWeightKg?: number | null;
    typicalHeightLowCm?: number | null;
    typicalHeightHighCm?: number | null;
    suggestedPhaseId?: number | null;
    suggestedPhaseName?: string | null;
};

export const getMeasurementSuggestions = () =>
    shopAxios
        .get<CurrentMeasurementSuggestionDto[]>('/children/measurement-suggestions')
        .then((res) => res.data);

export const confirmChildMeasurements = (
    childId: number,
    dto: { heightCm?: number | null; weightKg?: number | null }
) => shopAxios.post<ChildDto>(`/children/${childId}/measurements`, dto).then((res) => res.data);

export const skipChildMeasurements = (childId: number) =>
    shopAxios.post(`/children/${childId}/measurements/skip`);
//Checklists
export const updateChecklistItem = (childId: number, itemId: number, dto: { isChecked: boolean }) =>
    shopAxios.put(`/children/${childId}/checklist/${itemId}`, dto);
export const addCustomChecklistItem = async (childId: number, dto: { name: string; phaseId: number }) => {
    return await shopAxios.post(`/children/${childId}/checklist`, dto);
};
export const deleteChecklistItem = (childId: number, itemId: number) =>
    shopAxios.delete(`/children/${childId}/checklist/${itemId}`);

// Favourites/wishlist
export const getFavourites = () => shopAxios.get<Product[]>('/user/favourites').then(res => res.data);
export const addFavourite = (productId: number) => shopAxios.post(`/user/favourites/${productId}`);
export const removeFavourite = (productId: number) => shopAxios.delete(`/user/favourites/${productId}`);

// Functionalities//

/** Shop branding only (shopAxios). No fee/tax. */
export const getSiteSettings = async (): Promise<SiteSettings> => {
    const response = await shopAxios.get('/sitesettings');
    return response.data;
};

/** Shop-safe locale: country + optional domestic shipping amount (not fee %). */
export const getMarketplaceLocale = async (): Promise<ShopMarketplaceLocale> => {
    const response = await shopAxios.get('/marketplace/locale');
    return response.data;
};

//Search (q and/or location — city/region/province, never street)
// `filter` = sort mode (latest | price-asc | price-desc | popular); part of server cache key
export const searchProducts = (
    params: {
        q?: string;
        location?: string;
        page?: number;
        limit?: number;
        filter?: string;
        excludeSellerId?: string;
    } = { q: '', page: 1, limit: 20 }
) => {
    const cleaned: Record<string, string | number> = {};
    if (params.q?.trim()) cleaned.q = params.q.trim();
    if (params.location?.trim()) cleaned.location = params.location.trim();
    if (params.page != null) cleaned.page = params.page;
    if (params.limit != null) cleaned.limit = params.limit;
    if (params.filter?.trim()) cleaned.filter = params.filter.trim();
    if (params.excludeSellerId?.trim()) cleaned.excludeSellerId = params.excludeSellerId.trim();
    return shopAxios
        .get<PaginatedResponse<Product>>('/search/products', { params: cleaned })
        .then((res) => res.data);
};

// Sidebar filters for product filtering UI
export const getSidebarFilters = () =>
    shopAxios.get<SidebarFilterDto[]>('/sidebar-filters').then(res => res.data);

// Partner inquiry (public)
export const submitPartnerInquiry = (data: CreatePartnerInquiryDto) =>
    shopAxios.post('/partner/inquiries', data);

// Notifications
export const getNotifications = (onlyUnread = false, language?: string) =>
    shopAxios.get<NotificationDto[]>(`/notifications`, {
        params: { onlyUnread, ...(language ? { language } : {}) },
    }).then(res => res.data);

export const markNotificationAsRead = (id: string) =>
    shopAxios.post(`/notifications/${id}/read`);

export const markAllNotificationsAsRead = () =>
    shopAxios.post('/notifications/read-all');

// Follow
export const followUser = (userId: string) =>
    shopAxios.post(`/follow/${userId}`);

export const unfollowUser = (userId: string) =>
    shopAxios.delete(`/follow/${userId}`);

/** Private: only the authenticated user's own following list (never another user's). */
export const getMyFollowing = () =>
    shopAxios
        .get<import('../types').FollowingUserDto[]>('/follow/following')
        .then((res) => res.data);

export const getFollowFeed = (page = 1, pageSize = 30) =>
    shopAxios
        .get<import('../types').FollowFeedPageDto>(`/follow/feed`, { params: { page, pageSize } })
        .then((res) => res.data);

export const markFollowFeedSeen = () => shopAxios.post('/follow/feed/seen');

export const getFollowPreferences = () =>
    shopAxios.get<import('../types').FollowPreferencesDto>('/follow/preferences').then((res) => res.data);

export const setFollowPreferences = (dto: import('../types').FollowPreferencesDto) =>
    shopAxios.put<import('../types').FollowPreferencesDto>('/follow/preferences', dto).then((res) => res.data);

export const getAllLanguages = () =>
    shopAxios.get<LanguageDto[]>('/languages').then(res => res.data);

// Lightweight public versions for TanStack cache invalidation
export const getPublicVersions = () =>
    shopAxios.get<{ products: number; categories: number }>('/public/versions').then(res => res.data);
